<template>
  <div class='wrapper-page message-show'>
    <modal class="filter-all-messages" ref="deleteMessage">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.delete_messages') }}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_delete') }}</p>
          </div>
          <div class='modal-footer'>
            <input :value="$t('communication.delete_messages_button')" @click.prevent='deleteMessage(message)'
                   class='btn btn-outline-danger float-right' type='reset'/>
          </div>
        </form>
      </template>
    </modal>
    <modal ref="restoreMessage">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.restore_message')}}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_restore')}}</p>
          </div>
          <div class='modal-footer'>
            <input :value="$t('communication.restore_messages_button')" @click.prevent='restoreMessage(message)' class='btn btn-primary float-right' type='reset' />
          </div>
        </form>
      </template>
    </modal>

    <div class='container-fluid'>
      <p class="back-to-list"><a :href="'/communication/' + computedMessageType">
        <i class="fas fa-long-arrow-alt-left"></i>
        {{ $t('communication.back_to')}} {{ referrerName }}
      </a></p>
      <h1 class='page-title' v-if="message.subject">
        {{ message.subject }}
      </h1>
      <h1 class='page-title' v-else>
        {{ $t('communication.no_subject') }}
      </h1>
      <div class="persf" v-if="computedMessageType !== 'trash'">
        <a @click.prevent="toggleStarMessage(message)" class="starred" href='#'>
          <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
        </a>
      </div>
      <div class="message-tags-wrapper" v-if="computedMessageType !== 'trash'">
        <label>{{ $t('communication.message_show.tags') }}</label>
        <message-tag-manager :message="message" :message-type="tagMessageType" @success='getMessage($props.id, $props.messageType)' />
      </div>
      <loading v-if="loading" />
      <section class="message-wrapper" v-else>
        <header>
          <div class="dropdown float-right">
            <button aria-expanded="false" aria-haspopup="true" class="btn dropdown-toggle" data-toggle="dropdown"
                    type="button">· · ·
            </button>
            <div class="dropdown-menu dropdown-menu-right">
              <ul>
                <li v-if="computedMessageType === 'trash'">
                  <a @click.prevent='$refs.restoreMessage.open()' class="dropdown-item" href="#" rel="nofollow">
                    <i class="fas fa-trash-restore"></i>
                    {{ $t('communication.restore')}}
                  </a>
                </li>
                <li v-else>
                  <a @click.prevent='$refs.deleteMessage.open()' class="dropdown-item" href="#" rel="nofollow">
                    <i class="far fa-trash-alt"></i>
                    {{ $t('communication.delete') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="float-right">
            <p>
              <strong>{{ $t('communication.created_on') }}</strong>
              <span>{{ formatDate(message.created_at) }}</span>
            </p>
            <p v-if="message.status">
              <strong>{{ $t('communication.status') }}:</strong>
              <i :class="`fas fa-circle ${ statusClass(message.status) }`" />
              <span>&nbsp;{{ statusText(message.status) }}</span>
            </p>
            <p v-if="computedMessageType === 'future-emails'">
              <strong>{{ $t('communication.will_send_on') }}</strong>
              <span>{{ formatDate(message.future_sending_at) }}</span>
            </p>
          </div>

          <p v-if="computedMessageType === 'sent' || computedMessageType === 'future-emails'"><strong>{{ $t('communication.message_show.from') }}</strong>
            <span v-if="message.creator">{{ message.creator.full_name }}</span></p>
          <p v-if="computedMessageType === 'sent' || computedMessageType === 'future-emails'"><strong>{{ $t('communication.message_show.to') }}</strong>
            <span v-if="message.receiver">{{ message.receiver.name }} - {{ message.receiver.email }} - {{ message.receiver.company }}</span></p>
          <p v-if="computedMessageType === 'inbox' || computedMessageType === 'attachments'" class="only-from"><strong>{{ $t('communication.message_show.from') }}</strong>
            <span v-if="message.contact">{{ message.contact.name }} - {{ message.contact.company }}</span></p>
          <p v-if="computedMessageType === 'automated-emails'"><strong>
            {{ $t('communication.message_show.from') }}</strong>
            {{ message.from }}</p>
          <p v-if="computedMessageType === 'automated-emails'"><strong>{{
              $t('communication.message_show.to')
            }}</strong>
            <span v-if="message.contact">{{ message.contact.email }}</span></p>
          <p v-if="computedMessageType === 'trash'">
            <strong>{{ $t('communication.message_show.from') }}</strong>
            <span v-if="query.type === 'sent' && message.creator">{{ message.creator.full_name }}</span>
            <span v-if="query.type === 'inbox' && message.contact">{{ message.contact.name }} - {{ message.contact.company }}</span>
            <span v-if="query.type === 'automated-emails' && message.contact">{{ message.contact.name }} - {{ message.contact.company }}</span>
          </p>
          <p v-if="computedMessageType === 'trash' && query.type === 'sent'">
            <strong>{{ $t('communication.message_show.to') }}</strong>
            <span v-if="message.receiver">{{ message.receiver.name }} - {{ message.receiver.email }} - {{ message.receiver.company }}</span>
          </p>
        </header>
        <div class="message-body">
          <ul class="attachments" v-if="computedMessageType === 'inbox'">
            <li v-for="record in message.records">
              <img :src="mimeToSrc(record.mime_type)"/>
              <router-link :to="{name: 'record', params: { id: record.id }}" class="text-truncate file-name">
                {{ record.filename }}
              </router-link>
            </li>
          </ul>
          <ul class="attachments" v-if="computedMessageType === 'sent'">
            <li v-for="record in message.attached_records">
              <img :src="mimeToSrc(record.mime_type)"/>
              <router-link :to="{name: 'record', params: { id: record.id }}" class="text-truncate file-name">
                {{ record.filename }}
              </router-link>
            </li>
          </ul>
          <p v-html="message.body" v-if="message.body && computedMessageType === 'automated-emails'"></p>
          <p v-else-if="message.message" v-html="message.message"></p>
          <p v-else>{{ $t('communication.message_show.no_message') }}</p>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import _modal from "../../components/_modal";
import message_tag_manager from "../../components/tags/_message_tag_manager"
import _loading from '../../components/_loading'

export default {
  name: 'MessageShow',
  components: {
    'modal': _modal,
    'message-tag-manager': message_tag_manager,
    'loading': _loading,
  },
  data() {
    return {
      loading: true,
    }
  },
  props: {
    id: {
      required: true
    },
    hash: {},
    messageType: {
      type: String,
      required: true
    },
    referrer: {
      type: String,
      required: false
    }
  },
  created() {
    this.getMessage(this.$route.params.id, this.$props.messageType, this.$route.query.type)
  },

  computed: {
    message() {
      return this.state.message || {}
    },
    computedMessageType() {
      return this.$props.messageType
    },
    tagMessageType() {
      let type = this.$props.messageType
      if(this.$route.query.type !== undefined){
        type = this.$route.query.type
      }

      if (type === 'inbox') {
        return 'documentResponse'
      } else if (type === 'sent') {
        return 'documentRequest'
      } else if (type === 'automated-emails') {
        return 'automatedEmail'
      } else if (type === 'future-emails') {
        return 'documentRequest'
      }
    },
    referrerName() {
      let type = this.$props.messageType

      if (type === 'inbox') {
        return this.$t('communication.received_messages')
      } else if (type === 'sent') {
        return this.$t('communication.sent_messages')
      } else if (type === 'automated-emails') {
        return this.$t('communication.automated_emails')
      } else if (type === 'future-emails') {
        return this.$t('communication.future_emails')
      }
    }
  },

  methods: {
    getMessage(id, messageType, type) {
      this.store.getMessage(id, messageType, type).then(() =>  this.loading = false)
    },
    toggleStarMessage(message) {
      this.store.sendMetrics({
        name: 'star_message_from_message_show',
        params: this.$route.params,
        path: this.$route.fullPath
      })
      let type = this.tagMessageType

      if (type === 'documentResponse') {
        return this.store.updateDocumentResponse(message.id, {starred: !message.starred}).then(() => {
          this.getMessage(this.$route.params.id, this.$props.messageType, this.$route.query.type)
        })
      }
      if (type === 'documentRequest') {
        return this.store.updateDocumentRequest(message.id, { starred: !message.starred}).then(() => {
          this.getMessage(this.$route.params.id, this.$props.messageType, this.$route.query.type)
        })
      }
      if (type === 'automatedEmail') {
        return this.store.updateAutomatedEmails(message.id, { starred: !message.starred}).then(() => {
          this.getMessage(this.$route.params.id, this.$props.messageType, this.$route.query.type)
        })
      }
    },

    deleteMessage(message) {
      this.$refs.deleteMessage.close()
      this.store.sendMetrics({
        name: 'delete_message_from_message_show',
        params: this.$route.params,
        path: this.$route.fullPath
      })
      let type = this.tagMessageType
      if (type === 'documentResponse') {
        this.store.deleteDocumentResponse([message.id]).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }
      if (type === 'documentRequest') {
        this.store.deleteDocumentRequest([message.id]).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }
      if (type === 'automatedEmail') {
        this.store.deleteAutomatedEmail([message.id]).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }
    },
    restoreMessage(message) {
      this.$refs.restoreMessage.close()
      this.store.sendMetrics({name: 'communication_restore_bin', params: this.$route.params, path: this.$route.fullPath})
      let type = this.tagMessageType

      if (type === 'documentRequest') {
        this.store.restoreDocumentRequest(message.id).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }else if (type === 'documentResponse') {
        this.store.restoreDocumentResponse(message.id).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }else if (type === 'automatedEmail') {
        this.store.restoreAutomatedEmail(message.id).then(() => {
          this.$router.push({name: this.$props.messageType})
        })
      }
    },
  }

}
</script>

<style lang="sass" scoped>
 @import "../../styles/messages"
</style>
